import { useCallback, useState } from 'react';
import type { AdminOrganizationDetail, AdminUserRow } from '../../../../domain/superadmin/types';
import { SUPERADMIN_ROLE } from '../../../../domain/superadmin/guards';
import { deleteAdminUsers } from '../../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../../services/modules/superadmin/errors';
import { notify } from '../../../../utils/toast';
import DashboardCard from '../../../dashboard/components/DashboardCard';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui';
import { MoreHorizontalIcon } from '../../../../components/icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { DeleteUsersDialog } from '../../users/DeleteUsersDialog';
import { SectionEmpty } from './SectionEmpty';

type TeamMember = AdminOrganizationDetail['team'][number];

type TeamSectionProps = {
  team: AdminOrganizationDetail['team'];
  organizationId: string;
  onRefetch: () => Promise<void>;
};

function formatRole(role: string | null, globalRole: string | null) {
  if (globalRole === SUPERADMIN_ROLE) return 'Superadmin';
  if (role) return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return 'Membro';
}

function roleBadgeClass(globalRole: string | null) {
  if (globalRole === SUPERADMIN_ROLE) {
    return 'border-primary/30 bg-primary-subtle text-primary';
  }
  return 'border-border bg-surface-subtle text-secondary';
}

function teamMemberToAdminUserRow(member: TeamMember): AdminUserRow {
  return {
    id: member.userId,
    fullName: member.fullName,
    email: member.email,
    phone: null,
    globalRole: member.globalRole,
    organizationCount: member.role === 'owner' ? 1 : 0,
    createdAt: '',
  };
}

function MemberActionsMenu({
  member,
  onDelete,
}: {
  member: TeamMember;
  onDelete: (member: TeamMember) => void;
}) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-subtle hover:text-default"
          aria-label={`Ações para ${member.email}`}
        >
          <MoreHorizontalIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem variant="destructive" onClick={() => onDelete(member)}>
          Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MemberCard({
  member,
  onDelete,
}: {
  member: TeamMember;
  onDelete: (member: TeamMember) => void;
}) {
  const displayName = member.fullName?.trim() || member.email;

  return (
    <article className="rounded-xl border border-subtle bg-surface px-s4 py-3">
      <div className="flex items-start justify-between gap-s3">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-default">{displayName}</p>
          <p className="truncate text-[11.5px] text-muted">{member.email}</p>
        </div>
        <div className="flex shrink-0 items-center gap-s2">
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-medium',
              roleBadgeClass(member.globalRole),
            )}
          >
            {formatRole(member.role, member.globalRole)}
          </span>
          <MemberActionsMenu member={member} onDelete={onDelete} />
        </div>
      </div>
    </article>
  );
}

export function TeamSection({ team, organizationId, onRefetch }: TeamSectionProps) {
  const [deleteTargets, setDeleteTargets] = useState<AdminUserRow[] | null>(null);

  const handleDeleteOne = useCallback((member: TeamMember) => {
    setDeleteTargets([teamMemberToAdminUserRow(member)]);
  }, []);

  const handleDeleteConfirm = useCallback(
    async (ids: string[]) => {
      try {
        const result = await deleteAdminUsers(ids, { organizationId });
        if (result.deleted.length > 0) {
          notify.success(
            result.deleted.length === 1
              ? 'Usuário excluído com sucesso.'
              : `${result.deleted.length} usuários excluídos com sucesso.`,
          );
        }
        if (result.skipped.length > 0) {
          notify.warning(`${result.skipped.length} usuário(s) não puderam ser excluídos.`);
        }
        setDeleteTargets(null);
        await onRefetch();
      } catch (e) {
        notify.error(
          e instanceof SuperadminApiError || e instanceof Error
            ? e.message
            : 'Erro ao excluir usuários',
        );
        throw e;
      }
    },
    [organizationId, onRefetch],
  );

  return (
    <>
      <DashboardCard title="Equipe" className="mb-s5">
        {team.length === 0 ? (
          <SectionEmpty message="Nenhum membro encontrado nesta empresa." />
        ) : (
          <>
            <div className="flex flex-col gap-s3 md:hidden">
              {team.map((member) => (
                <MemberCard key={member.userId} member={member} onDelete={handleDeleteOne} />
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-xl border border-subtle md:block">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-subtle hover:bg-transparent">
                    <TableHead className="bg-card text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                      Nome
                    </TableHead>
                    <TableHead className="bg-card text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                      E-mail
                    </TableHead>
                    <TableHead className="bg-card text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                      Papel
                    </TableHead>
                    <TableHead className="w-10 bg-card">
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.map((member) => (
                    <TableRow
                      key={member.userId}
                      className="border-b border-subtle last:border-b-0"
                    >
                      <TableCell>
                        <span className="text-[13px] font-medium text-default">
                          {member.fullName?.trim() || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-[13px] text-secondary">{member.email}</span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium',
                            roleBadgeClass(member.globalRole),
                          )}
                        >
                          {formatRole(member.role, member.globalRole)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <MemberActionsMenu member={member} onDelete={handleDeleteOne} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DashboardCard>

      <DeleteUsersDialog
        open={deleteTargets !== null && deleteTargets.length > 0}
        users={deleteTargets ?? []}
        onClose={() => setDeleteTargets(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
